<template>
  <div>
    <header class="app-header">
      <div class="header__inner">
        <div class="header__title-bar" v-if="hasPermissionUnlimitedSearch">
          <h1 class="header__title">Jobskills</h1>
          <h2 class="header__subtitle">Sök</h2>
        </div>
        <div class="header__title-bar" v-else>
          <router-link to="/" class="header__title-bar-wrap">
            <img class="header__logo" :src="`${baseURL}static/img/jobskills_logo.png`" />
          </router-link>
            <div class="separator"></div>
            <img class="header__logo-af" :src="`${baseURL}static/img/af_logo_new_dark.svg`" />
        </div>

        <div class="header__switch-wrap" :class="{
          'header__switch-wrap-hidden': loggedIn
        }">
          <a :href="jobskillsURL" class="header__switch-external">Arbetssökande</a>
          <router-link to="/" class="header__switch-internal">Arbetsgivare</router-link>
        </div>

        <div class="header__login-wrap">
          <div class="logged-in" v-if="loggedIn">
            <af-button @clicked="logout"
              class="logged-in__white-btn"
              lowercase
              buttonStyle="default">
              <icon slot="icon" type="user"></icon>
              Logga ut
            </af-button>
            <af-button @clicked="logout"
              class="logged-out__blue-btn"
              lowercase
              buttonStyle="primary">
              <icon fill="white" slot="icon" type="user"></icon>
              Logga ut
            </af-button>
          </div>
          <div class="logged-out" v-else>
            <af-button
              class="logged-out__white-btn"
              @clicked="login"
              lowercase
              buttonStyle="default">
                <icon slot="icon" type="user"></icon> Logga in
            </af-button>
            <af-button
              class="logged-out__blue-btn"
              @clicked="login"
              lowercase
              buttonStyle="primary">
                <icon fill="white" slot="icon" type="user"></icon> Logga in
            </af-button>
          </div>
          <af-button @clicked="toggleSidebarOpen" class="header__logo_menu" lowercase buttonStyle="primary">
              <icon fill="white" slot="icon" type="bars"></icon>
            Meny
          </af-button>
        </div>
      </div>
    </header>

    <div class="header__sidemenu" :class="{
      'header__sidemenu_open': sidebarOpen,
      'header__sidemenu_close': !sidebarLoading && !sidebarOpen
    }">
      <af-sidemenu @clicked="toggleSidebarOpen"></af-sidemenu>
    </div>

    <header class="app-header-navigation" v-if="hasPermissionUserEmailBatches">
      <div class="header__inner">
        <ul class="header__navigation">
          <li>
            <router-link to="start">Sök</router-link>
            <router-link to="utskick">Utskick</router-link>
          </li>
        </ul>
      </div>
    </header>
  </div>
</template>

<script>
import Button from '@/components/Button/Button'
import Icon from '@/components/Icon'
import ExternalLink from '@/components/ExternalLink'
import AfSidemenu from '@/components/AfSidemenu'
import { mapGetters } from 'vuex'
import { deconstructJWT } from '@/utils/helpers'
import * as types from '@/store/actionTypes'
import cookieUtils from '@/utils/cookies'

export default {
  name: 'AppHeader',
  components: {
    'af-button': Button,
    'af-sidemenu': AfSidemenu,
    Icon,
    ExternalLink
  },
  data() {
    return {
      jobskillsURL: window.__JOBSKILLS_URL__,
      baseURL: window.__BASE_URL__,
      sidebarOpen: false,
      sidebarLoading: true
    }
  },
  computed: {
    ...mapGetters([
      'loggedIn',
      'hasPermissionUserEmailBatches',
      'hasPermissionUnlimitedSearch'
    ])
  },
  created() {
    this.verifyToken()
  },
  methods: {
    logout() {
      this.$store.commit(types.LOG_OUT)
      cookieUtils.removeItem('search-token')
      this.$router.push('/logged-out')
    },
    login() {
      window.location.href = `${__API_URL__}auth/saml?returnUrl=${__BASE_URL__}sok`
    },
    toggleSidebarOpen() {
      this.sidebarOpen = !this.sidebarOpen
      this.sidebarLoading = false
    },
    verifyToken() {
      if (!this.loggedIn && !cookieUtils.hasItem('search-token')) {
        return
      }
      if (!cookieUtils.hasItem('search-token')) {
        this.$store.commit(types.LOG_OUT)
        this.$router.push('/')
        return
      }

      const token = cookieUtils.getItem('search-token')
      const tokenExpire = deconstructJWT(token).exp * 1000

      if (Date.now() > tokenExpire) {
        cookieUtils.removeItem('search-token')
        this.$store.commit(types.LOG_OUT)
        this.$router.push('/')
      } else {
        const timeUntilExpire = tokenExpire - Date.now()
        setTimeout(this.verifyToken, timeUntilExpire)
        this.$store.commit(types.LOG_IN)
      }
    }
  }
}
</script>

<style lang="postcss" scoped>
@import '../../variables/custom-media.css';
.header__inner {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-left: auto;
  margin-right: auto;
  width: 100%;

  @supports (display: grid) {
    display: grid;
    grid-template-columns: 1fr;
    max-width: 100%;
    width: 100%;
  }

  @media (--bp-nis-small) {
    flex-direction: row;
    grid-template-columns: 1fr 1fr auto;
  }

  @media (--bp-nis-medium) {
    padding-left: 20px;
    padding-right: 20px;
  }

  @media (--bp-desktop-xlarge) {
    padding-left: 0;
    padding-right: 0;
    width: var(--spacing-max-width-large);
  }
}

.app-header {
  background-color: var(--color-white);
  color: var(--color-mine-shaft);
  display: flex;
  width: 100%;
  box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.158);
  position: relative;

  @supports (display: grid) {
    grid-area: header;
  }

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    -ms-grid-column: 1;
    -ms-grid-row: 1;
  }

  @supports (-ms-ime-align: auto) {
    -ms-grid-column: 1;
    -ms-grid-row: 1;
  }

  & .header__title-bar {
    align-items: center;
    display: flex;
    padding: var(--spacing-half);

    @media (--bp-nis-medium) {
      padding: 0;
    }
  }

  & .header__title-bar-wrap {
    align-items: center;
    display: flex;
    color: black;
    text-decoration: none;
  }

  & .header__title {
    color: var(--color-white);
    font-size: 25px;
    font-weight: 400;
    margin: 0;
    margin-right: 5px;

    @media (--bp-iphone6) {
      font-size: 20px;
    }
  }

  & .header__subtitle {
    font-size: 21px;
    font-weight: 300;
    margin: 0;
    margin-top: 4px;
  }

  & .header__login-wrap {
    align-items: center;
    display: flex;

    @media (--bp-large-2) {
      background-color: var(--color-allports);
      justify-content: space-between;
      padding: 0 var(--spacing);
    }
  }

  & .header__logo_menu {
    display: flex;

    @media (min-width: 54.5em) {
      display: none;
    }
  }

  & .logged-out__blue-btn,
  & .logged-in__blue-btn {
    display: none;
    @media (--bp-large-2) {
      display: flex;
    }
  }

  & .logged-out__white-btn,
  & .logged-in__white-btn {
    display: flex;

    @media (--bp-large-2) {
      display: none;
    }
  }

  & .lathund {
    margin-right: var(--spacing-medium);

    & a {
      color: var(--color-matisse);
    }
  }

  & .header__logo {
    margin-right: var(--spacing);
    margin-top: -10px;
    max-width: var(--spacing-large);

    @media (--bp-large2) {
      margin-right: var(--spacing-medium);
      max-width: var(--spacing);
    }
  }

  & .separator {
    height: var(--spacing);
    border-left: 1px solid var(--color-iron);
    margin-right: var(--spacing);
  }

  & .header__logo-af {
    width: 53%;
    max-width: 300px;

    @media (--ie11) {
      width: auto;
    }
  }
}

.header__switch-wrap {
  align-items: flex-end;
  display: flex;

  @media (--bp-large-2) {
    display: none;
  }

  & a {
    text-decoration: none;
    font-weight: 600;
  }

  & a:hover {
    text-decoration: underline;
  }

  & .header__switch-external {
    color: var(--color-rolling-stone);
    margin-right: var(--spacing-half);
    padding: var(--spacing) 0 var(--spacing);
  }

  & .header__switch-internal,
  & > .router-link-exact-active {
    border-bottom: 5px solid var(--color-allports);
    color: black;
    padding: 25px 0;
  }
}

.header__switch-wrap-hidden {
  visibility: hidden;
}

.header__sidemenu {
  position: fixed;
  top: 0;
  bottom: 0;
  transform: translateX(120%);
  width: 90%;
  height: 100%;
  height: 100%;
  z-index: 3;
}

@keyframes slide {
  100% {
    transform: translateX(12%);
  }
}

@keyframes slide-out {
  0% {
    transform: translateX(12%);
  }
  100% {
    transform: translateX(120%);
  }
}

.header__sidemenu_close {
  animation: slide-out 0.25s forwards;
}

.header__sidemenu_open {
  animation: slide 0.25s forwards;
}

.app-header-navigation {
  background-color: var(--color-pickled-bluewood);

  & .header__navigation {
    & li {
      display: flex;
      flex-direction: row;
    }

    & a:not(:last-of-type)::after {
      content: '';
      position: absolute;
      right: 0;
      top: var(--spacing-half);
      width: 1px;
      height: 22px;
      border-right: 1px solid var(--color-bismark);
    }

    & a {
      position: relative;
      text-decoration: none;
      padding: var(--spacing-half) var(--spacing);
      border-bottom: 2px solid transparent;

      &:link,
      &:visited,
      &:hover,
      &:active {
        color: var(--color-white);
      }

      &:hover,
      &:active {
        border-bottom: 2px solid var(--color-pomegranate);
      }
    }

    & a.router-link-active {
      border-bottom: 2px solid var(--color-pomegranate);
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/App/AppHeader.vue